import { z } from "zod";
import { paymentMethodSchema } from "./billing.ts";
import { businessTypeSchema, facilityTypeSchema } from "./clinic.ts";
import { billingIntervalSchema, subscriptionTierSchema } from "./clinic.ts";

// ===== Platform admin (super-admin console) =====

export const adminLoginSchema = z.object({
	email: z.string().email(),
	password: z.string().min(1),
});
export type AdminLoginInput = z.infer<typeof adminLoginSchema>;

export interface AdminAuthResponse {
	user: { id: string; full_name: string; email: string | null };
	tokens: { access_token: string; refresh_token: string; expires_in: number };
}

// ── Super-admin 2FA (TOTP) ──────────────────────────────────────────────────

/** Login on a TOTP-enabled account returns this instead of a session. */
export interface AdminTotpChallengeApi {
	requires_totp: true;
	/** Short-lived challenge to exchange (with a code) at /admin/login/totp. */
	totp_token: string;
}
export type AdminLoginResult = AdminAuthResponse | AdminTotpChallengeApi;

export const adminTotpVerifySchema = z.object({
	totp_token: z.string().min(1),
	/** 6-digit TOTP or a backup code (xxxx-xxxx). */
	code: z.string().min(6).max(12),
});
export type AdminTotpVerifyInput = z.infer<typeof adminTotpVerifySchema>;

export const adminTotpCodeSchema = z.object({
	code: z.string().min(6).max(12),
});
export type AdminTotpCodeInput = z.infer<typeof adminTotpCodeSchema>;

export interface AdminTotpStatusApi {
	enabled: boolean;
	/** A secret was generated but not yet confirmed with a code. */
	pending: boolean;
}

export interface AdminTotpSetupApi {
	secret: string;
	otpauth_uri: string;
}

export interface AdminTotpEnableApi {
	/** Single-use recovery codes — shown exactly once. */
	backup_codes: string[];
}

/** One facility (tenant) row in the admin console. */
export const adminFacilitySchema = z.object({
	id: z.string().uuid(),
	slug: z.string(),
	name: z.object({ ar: z.string(), en: z.string().optional() }),
	facility_type: facilityTypeSchema,
	business_type: businessTypeSchema,
	subscription_tier: subscriptionTierSchema,
	trial_ends_at: z.string().datetime().nullable(),
	subscribed_until: z.string().datetime().nullable(),
	is_active: z.boolean(),
	feature_flags: z.record(z.string(), z.boolean()),
	owner_name: z.string().nullable(),
	owner_email: z.string().nullable(),
	/** True when the owner account is currently locked (failed-login lockout). */
	owner_locked: z.boolean(),
	created_at: z.string().datetime(),
	/** Most recent audited action for this facility (null = no activity logged). */
	last_activity_at: z.string().datetime().nullable(),
});
export type AdminFacilityApi = z.infer<typeof adminFacilitySchema>;

// ===== Mutations (used in the next slice) =====

export const adminCreateFacilitySchema = z.object({
	facility_type: facilityTypeSchema.default("clinic"),
	// Clinical specialty — drives specialty UI (e.g. the dental odontogram).
	// Labs/pharmacies stay 'general'. Defaults to 'general' for back-compat.
	business_type: businessTypeSchema.default("general"),
	name_ar: z.string().min(2).max(120),
	name_en: z.string().max(120).optional(),
	slug: z
		.string()
		.min(2)
		.max(50)
		.regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/u, "Invalid slug"),
	owner_full_name: z.string().min(2).max(120),
	owner_email: z.string().email(),
	owner_password: z.string().min(8).max(128),
	subscription_tier: subscriptionTierSchema.default("trial"),
	subscribed_until: z.string().datetime().nullable().optional(),
});
export type AdminCreateFacilityInput = z.infer<
	typeof adminCreateFacilitySchema
>;

export const adminSetSubscriptionSchema = z.object({
	subscription_tier: subscriptionTierSchema,
	subscribed_until: z.string().datetime().nullable(),
	// Agreed monthly price (IQD). Optional so existing callers keep working;
	// null clears it (falls back to a per-type anchor in MRR).
	monthly_price: z.number().min(0).max(99_999_999).nullable().optional(),
	// Provider seat cap (owner + doctor + lab_tech). Optional = leave as-is.
	max_doctors: z.number().int().min(1).max(500).optional(),
	// Inpatient/HIS capability (opt-in). Optional = leave as-is.
	inpatient_enabled: z.boolean().optional(),
	// Renewal cadence (drives the one-click renew). Optional = leave as-is.
	billing_interval: billingIntervalSchema.optional(),
});
export type AdminSetSubscriptionInput = z.infer<
	typeof adminSetSubscriptionSchema
>;

// ===== Smart renewal =====
// One-click renew: extends subscribed_until from the LATER of (current expiry,
// today) by the chosen interval — so renewing early never loses paid days and
// never overlaps, and renewing after expiry restarts from today. Also reactivates
// a facility that was suspended, and can record the matching payment atomically.
export const adminRenewSubscriptionSchema = z.object({
	// Defaults to the facility's stored billing_interval when omitted.
	interval: billingIntervalSchema.optional(),
	// Explicit month count override (e.g. a custom 6-month deal). Wins over interval.
	months: z.number().int().min(1).max(60).optional(),
	// Record a subscription_payment for this renewal in the same transaction.
	record_payment: z.boolean().default(false),
	// Payment amount; defaults to monthly_price × months when omitted.
	amount: z.number().positive().max(99_999_999).optional(),
	method: paymentMethodSchema.default("cash"),
	note: z.string().max(500).optional(),
});
export type AdminRenewSubscriptionInput = z.infer<
	typeof adminRenewSubscriptionSchema
>;

export const adminRenewResultSchema = z.object({
	id: z.string().uuid(),
	subscribed_until: z.string().datetime(),
	billing_interval: billingIntervalSchema,
	months_added: z.number().int(),
	payment_recorded: z.boolean(),
});
export type AdminRenewResult = z.infer<typeof adminRenewResultSchema>;

// ===== Subscription payments (platform revenue ledger) =====

export const adminSubscriptionPaymentSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	amount: z.string(),
	currency: z.string(),
	method: paymentMethodSchema,
	note: z.string().nullable(),
	paid_at: z.string().datetime(),
	created_at: z.string().datetime(),
});
export type AdminSubscriptionPaymentApi = z.infer<
	typeof adminSubscriptionPaymentSchema
>;

export const adminRecordPaymentSchema = z.object({
	amount: z.number().positive().max(99_999_999),
	method: paymentMethodSchema.default("cash"),
	paid_at: z.string().datetime().optional(),
	note: z.string().max(500).optional(),
	// Optionally extend the facility's subscribed_until in the same step.
	extend_until: z.string().datetime().nullable().optional(),
});
export type AdminRecordPaymentInput = z.infer<typeof adminRecordPaymentSchema>;

/** Void/reverse a mis-recorded subscription payment (records a reversing entry). */
export const adminVoidPaymentSchema = z.object({
	reason: z.string().trim().min(1).max(500),
});
export type AdminVoidPaymentInput = z.infer<typeof adminVoidPaymentSchema>;

export const adminSetStatusSchema = z.object({ is_active: z.boolean() });
export type AdminSetStatusInput = z.infer<typeof adminSetStatusSchema>;

export const adminSetBusinessTypeSchema = z.object({
	business_type: businessTypeSchema,
});
export type AdminSetBusinessTypeInput = z.infer<
	typeof adminSetBusinessTypeSchema
>;

// ===== Account recovery (super-admin acting on a facility's owner) =====

export const adminResetOwnerPasswordSchema = z.object({
	new_password: z.string().min(8).max(128),
});
export type AdminResetOwnerPasswordInput = z.infer<
	typeof adminResetOwnerPasswordSchema
>;

export const adminChangeOwnerEmailSchema = z.object({
	email: z.string().email(),
});
export type AdminChangeOwnerEmailInput = z.infer<
	typeof adminChangeOwnerEmailSchema
>;

// ===== Facility detail (drill-in) =====

export const adminFacilityMemberSchema = z.object({
	id: z.string().uuid(),
	user_id: z.string().uuid(),
	full_name: z.string(),
	email: z.string().nullable(),
	role: z.string(),
	title: z.string().nullable(),
	is_active: z.boolean(),
	locked: z.boolean(),
	joined_at: z.string().datetime(),
});
export type AdminFacilityMemberApi = z.infer<typeof adminFacilityMemberSchema>;

export const adminFacilityStatsSchema = z.object({
	members: z.number(),
	patients: z.number(),
	appointments: z.number(),
	visits: z.number(),
	invoices: z.number(),
	last_activity_at: z.string().datetime().nullable(),
});
export type AdminFacilityStatsApi = z.infer<typeof adminFacilityStatsSchema>;

export const adminFacilityDetailSchema = adminFacilitySchema.extend({
	timezone: z.string(),
	default_language: z.string(),
	monthly_price: z.string().nullable(),
	/** Provider seat cap (owner + doctor + lab_tech). */
	max_doctors: z.number().int(),
	/** Inpatient/HIS capability (opt-in). */
	inpatient_enabled: z.boolean(),
	/** Renewal cadence. */
	billing_interval: billingIntervalSchema,
	members: z.array(adminFacilityMemberSchema),
	stats: adminFacilityStatsSchema,
});
export type AdminFacilityDetailApi = z.infer<typeof adminFacilityDetailSchema>;

// ===== Facility links (super-admin managed clinic<->lab) =====

export const adminFacilityLinkSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	clinic_name: z.string(),
	/** Partner facility (lab or pharmacy). lab_id kept for back-compat = partner id. */
	lab_id: z.string().uuid(),
	lab_name: z.string(),
	partner_type: z.string(),
	note: z.string().nullable(),
	created_at: z.string().datetime(),
});
export type AdminFacilityLinkApi = z.infer<typeof adminFacilityLinkSchema>;

export const adminCreateLinkSchema = z.object({
	clinic_id: z.string().uuid(),
	lab_id: z.string().uuid(),
	note: z.string().max(300).optional(),
});
export type AdminCreateLinkInput = z.infer<typeof adminCreateLinkSchema>;

// ===== Platform overview (super-admin dashboard) =====

export const adminOverviewSchema = z.object({
	facilities: z.object({
		total: z.number(),
		active: z.number(),
		suspended: z.number(),
		clinics: z.number(),
		labs: z.number(),
		pharmacies: z.number(),
		radiology_centers: z.number(),
		new_last_30d: z.number(),
	}),
	subscriptions: z.object({
		trial: z.number(),
		paid_active: z.number(),
		expiring_soon: z.number(), // paid, ≤ 7 days left
		expired: z.number(),
	}),
	usage: z.object({
		patients: z.number(),
		appointments: z.number(),
		invoices: z.number(),
	}),
	/** Rough MRR estimate (IQD) from per-facility-type prices on active paid tenants. */
	estimated_mrr: z.number(),
	/** Actual subscription cash collected (from subscription_payments). */
	revenue: z.object({
		collected_this_month: z.number(),
		collected_total: z.number(),
		payments_this_month: z.number(),
	}),
	currency: z.string(),
});
export type AdminOverviewApi = z.infer<typeof adminOverviewSchema>;

// ===== AI cost meter (super-admin) =====

/** Estimated AI spend over a window, from ai_assist_log token columns. */
export interface AdminAiUsageApi {
	from: string;
	to: string;
	totals: {
		calls: number;
		inputTokens: number;
		outputTokens: number;
		cacheReadTokens: number;
		cacheCreationTokens: number;
		/** Estimated USD spend over the window. */
		costUsd: number;
		/** USD the prompt cache saved vs paying full input price on cached reads. */
		cacheSavingsUsd: number;
	};
	by_model: { model: string; calls: number; cost_usd: number }[];
	by_kind: { kind: string; calls: number; cost_usd: number }[];
	by_clinic: {
		clinic_id: string;
		clinic_name: string | null;
		calls: number;
		cost_usd: number;
	}[];
}

// ===== Platform announcement (super-admin → clinic TV ticker) =====

export const adminAnnouncementSchema = z.object({
	message_ar: z.string().max(280),
	message_en: z.string().max(280).optional().default(""),
	link_url: z.string().url().max(500).nullable().optional(),
	enabled: z.boolean(),
	starts_at: z.string().datetime().nullable().optional(),
	ends_at: z.string().datetime().nullable().optional(),
});
export type AdminAnnouncementInput = z.infer<typeof adminAnnouncementSchema>;

/** Current announcement as returned to the super-admin console. */
export interface AdminAnnouncementApi {
	message_ar: string;
	message_en: string;
	link_url: string | null;
	enabled: boolean;
	starts_at: string | null;
	ends_at: string | null;
	updated_at: string | null;
}

export const adminAuditEntrySchema = z.object({
	id: z.string(),
	occurred_at: z.string().datetime(),
	user_id: z.string().uuid().nullable(),
	action: z.string(),
	resource_type: z.string(),
	resource_id: z.string().uuid().nullable(),
});
export type AdminAuditEntryApi = z.infer<typeof adminAuditEntrySchema>;

/** A recent platform-wide activity item (audit log joined to its clinic). */
export const adminActivityItemSchema = z.object({
	id: z.string(),
	occurred_at: z.string().datetime(),
	action: z.string(),
	resource_type: z.string(),
	clinic_id: z.string().uuid().nullable(),
	clinic_name: z.string().nullable(),
	actor_name: z.string().nullable(),
});
export type AdminActivityItemApi = z.infer<typeof adminActivityItemSchema>;

// ===== Multi-branch groups (chains) =====

export const adminGroupSchema = z.object({
	id: z.string().uuid(),
	name: z.string(),
	created_at: z.string().datetime(),
	clinic_count: z.number(),
	clinics: z.array(
		z.object({
			id: z.string().uuid(),
			name: z.object({ ar: z.string(), en: z.string().optional() }),
			slug: z.string(),
		}),
	),
});
export type AdminGroupApi = z.infer<typeof adminGroupSchema>;

export const adminCreateGroupSchema = z.object({
	name: z.string().min(2).max(120),
	clinic_ids: z.array(z.string().uuid()).min(1).max(50),
	// Optional: ensure this user is an active owner-member of every branch so
	// they can switch between them under one login. Identified by email.
	owner_email: z.string().email().optional().or(z.literal("")),
});
export type AdminCreateGroupInput = z.infer<typeof adminCreateGroupSchema>;
